// Adapted from eval/dashboard/internal_web/src/components/session/turnBelief.ts (ATM-57): indexed off live turn rows. Keep in step until ATM-60 shares it.
/**
 * Which belief the differential panel shows beside an exchange, and what it is compared with —
 * the eval dashboard's `turnBelief`, read off the live session's turn rows.
 *
 * Every live turn row is one exchange: Jiva's question and the patient's answer to it (turn 0
 * is the opening description, `question_text` "Chief complaint"). The backend stamps med-
 * reasoning's ranked differential on the ANSWER row that produced it (`mr_state_snapshot`), so
 * the panel beside an exchange shows the belief that answer left. Rows flagged `is_inferred`
 * are questions med-reasoning answered itself: nobody was asked, they carry no belief of their
 * own, and they are not answers — the stepper and the scroll pass them by.
 *
 * `[]` is not `null`: an empty differential means Jiva was asked and its graph ranked nothing at
 * all, the single most important thing the panel can report about that answer; `null` means no
 * belief was stored — still on its way (the stamp commits one poll after the row), a side call
 * that failed, or a session from before per-answer snapshots.
 */

import type { DiseaseSnapshot, TurnView } from "../../api/types";

type Belief = readonly DiseaseSnapshot[] | null;

/** The fields this module reads, rather than a whole `TurnView`. */
type BeliefTurn = Pick<
  TurnView,
  "turn_number" | "is_inferred" | "mr_state_snapshot" | "created_at"
>;

/** One answer and the belief it produced. */
export type AnswerBelief = {
  /** The row's `turn_number` — the exchange's identity in the transcript. */
  turnNumber: number;
  /** 1-based over answers, inferred rows excluded; answer 1 is the opening description. */
  answerNumber: number;
  /** In rank order; `null` when none was stored. */
  differential: Belief;
  /** When the answer row was stored, in epoch ms; `NaN` if unparseable. */
  createdAt: number;
};

/** Every answer's belief, resolved once per poll. */
export type BeliefIndex = {
  answers: AnswerBelief[];
  byTurn: ReadonlyMap<number, AnswerBelief>;
};

/** Turn rows in transcript order. The API sends them ordered; this does not rely on it. */
export function inTurnOrder<T extends Pick<TurnView, "turn_number">>(turns: readonly T[]): T[] {
  return [...turns].sort((a, b) => a.turn_number - b.turn_number);
}

/** One pass over the rows. */
export function indexBeliefs(turns: readonly BeliefTurn[]): BeliefIndex {
  const answers: AnswerBelief[] = inTurnOrder(turns)
    .filter((turn) => !turn.is_inferred)
    .map((turn, position) => ({
      turnNumber: turn.turn_number,
      answerNumber: position + 1,
      differential:
        turn.mr_state_snapshot === null
          ? null
          : [...turn.mr_state_snapshot].sort((a, b) => a.rank - b.rank),
      createdAt: Date.parse(turn.created_at),
    }));
  return { answers, byTurn: new Map(answers.map((answer) => [answer.turnNumber, answer])) };
}

/** What the panel prints beside one exchange. */
export type ExchangeBelief = {
  /** The belief shown; `null` when this exchange's answer stored none. */
  differential: Belief;
  /** The answer the belief came from, 1-based; `null` for an exchange with no answer. */
  answerNumber: number | null;
  /** How many answers the session holds so far, for "answer n of m". */
  answers: number;
  /** The nearest earlier answer's stored belief, which the changes are measured against. */
  previous: Belief;
  /** Which answer `previous` is — not always n − 1, since an answer can have stored none. */
  previousAnswerNumber: number | null;
};

/** Resolve the belief beside the exchange at `turnNumber`. */
export function resolveExchangeBelief(
  index: BeliefIndex,
  turnNumber: number | null,
): ExchangeBelief {
  const answers = index.answers.length;
  const own = turnNumber === null ? undefined : index.byTurn.get(turnNumber);
  if (own === undefined) {
    return {
      differential: null,
      answerNumber: null,
      answers,
      previous: null,
      previousAnswerNumber: null,
    };
  }
  const before = index.answers
    .slice(0, own.answerNumber - 1)
    .filter((answer) => answer.differential !== null)
    .at(-1);
  return {
    differential: own.differential,
    answerNumber: own.answerNumber,
    answers,
    previous: before?.differential ?? null,
    previousAnswerNumber: before?.answerNumber ?? null,
  };
}

/**
 * The exchanges worth landing on, by turn number: those whose answer stored a belief. A
 * finished session that stored none at all lands on every answer instead, so what Jiva knew
 * after each one ("Known so far") stays reachable.
 */
export function steppableTurns(index: BeliefIndex, active: boolean): number[] {
  const withBelief = index.answers.filter((answer) => answer.differential !== null);
  const landing = withBelief.length > 0 || active ? withBelief : index.answers;
  return landing.map((answer) => answer.turnNumber);
}

/**
 * How long an answer's belief counts as on its way. The backend stamps it within seconds of the
 * row; past this the call that carries it failed, or the session was left active.
 */
export const PENDING_LIMIT_MS = 60_000;

/** An answer whose belief is still expected, and until when (epoch ms). */
export type PendingAnswer = { turnNumber: number; until: number };

/**
 * The answer whose belief is still on its way, if any: on an active session, the newest answer
 * row before its stamp lands. Told apart from "none was stored" so the panel can say which; the
 * caller drops it once `until` passes.
 */
export function pendingAnswer(index: BeliefIndex, active: boolean): PendingAnswer | null {
  const newest = index.answers.at(-1);
  if (!active || newest === undefined || newest.differential !== null) return null;
  const until = Number.isNaN(newest.createdAt) ? Infinity : newest.createdAt + PENDING_LIMIT_MS;
  return { turnNumber: newest.turnNumber, until };
}

/** The caption the stepper carries: whose belief this is, in the answers' own numbering. */
export function beliefCaption(belief: ExchangeBelief, active: boolean): string {
  const { answerNumber, answers } = belief;
  if (answerNumber === null) return "No answer has produced a belief yet";
  const which = `answer ${answerNumber} of ${answers}${active ? " so far" : ""}`;
  return answerNumber === 1 ? `After ${which}, the opening description` : `After ${which}`;
}

/** Whether a belief has entries to rank; every other state is a gap the panel names. */
export function hasEntries(belief: ExchangeBelief): boolean {
  return belief.differential !== null && belief.differential.length > 0;
}
